// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Local media capture and audio-level metering. A call wants the browser's
 * echo-cancellation / noise-suppression / auto-gain ON (unlike the measurement
 * tools in this fleet), so speech stays clean and there's no feedback howl.
 */
import { levelFromRms } from './mesh.ts';

export interface DeviceList {
  cams: MediaDeviceInfo[];
  mics: MediaDeviceInfo[];
}

/** Request the local camera + microphone. Throws on denial — the caller falls back to view-only. */
export async function getLocalStream(opts: { camId?: string; micId?: string; video: boolean; audio: boolean }): Promise<MediaStream> {
  const constraints: MediaStreamConstraints = {
    video: opts.video
      ? {
          deviceId: opts.camId ? { exact: opts.camId } : undefined,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user',
        }
      : false,
    audio: opts.audio
      ? {
          deviceId: opts.micId ? { exact: opts.micId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      : false,
  };
  return navigator.mediaDevices.getUserMedia(constraints);
}

/** Request a screen-share stream (video, plus system audio where the OS allows it). */
export async function getDisplayStream(): Promise<MediaStream> {
  return navigator.mediaDevices.getDisplayMedia({ video: { frameRate: { ideal: 15 } }, audio: true });
}

export async function listDevices(): Promise<DeviceList> {
  const all = await navigator.mediaDevices.enumerateDevices();
  return {
    cams: all.filter((d) => d.kind === 'videoinput'),
    mics: all.filter((d) => d.kind === 'audioinput'),
  };
}

/** Stop every track on a stream and let the OS camera/mic indicator go out. */
export function stopStream(stream?: MediaStream | null): void {
  if (!stream) return;
  for (const t of stream.getTracks()) t.stop();
}

/**
 * Taps an audio stream with an AnalyserNode to expose a smoothed 0..1 level for
 * a tile ring. Never connected to the destination, so tapping the local mic
 * causes no echo. Uses one shared AudioContext.
 */
export class AudioMeter {
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private buf: Float32Array<ArrayBuffer>;
  private lastRms = 0;

  constructor(ctx: AudioContext, stream: MediaStream) {
    this.buf = new Float32Array(1024);
    try {
      if (stream.getAudioTracks().length > 0) {
        this.source = ctx.createMediaStreamSource(stream);
        this.analyser = ctx.createAnalyser();
        this.analyser.fftSize = 2048;
        this.source.connect(this.analyser);
      }
    } catch {
      /* no audio track — level stays 0 */
    }
  }

  /** Current perceptual level 0..1. */
  read(): number {
    if (!this.analyser) return 0;
    this.analyser.getFloatTimeDomainData(this.buf);
    let sum = 0;
    for (let i = 0; i < this.buf.length; i++) sum += this.buf[i] * this.buf[i];
    this.lastRms = Math.sqrt(sum / this.buf.length);
    return levelFromRms(this.lastRms);
  }

  close(): void {
    try {
      this.source?.disconnect();
      this.analyser?.disconnect();
    } catch {
      /* already gone */
    }
    this.analyser = null;
    this.source = null;
  }
}
